import {
  pagableDataConverter,
  exportCsvConverter,
} from 'data/convertors/general.converters';
import {
  TPageableDataWithContentDB,
  TExportCsvDB,
  TExportCsvUI,
} from 'data/types/generic.types';
import {
  GetIoTransactionsParams,
  TCategoryTypeEnum,
  TIoCategoryDb,
  TIoCategoryOption,
  TUploadIoDocumentUI,
  TDeleteIoDocumentsUI,
  TIoDocumentResponse,
  TCreateInputOutputRequest,
  TCreateInputOutputResponse,
  TUpdateInputOutputRequest,
  TInputOutputRow,
  TIoTransactionsListDB,
  TIoTransactionsListUI,
} from 'data/types/io.types';
import { EIoUrls } from 'data/urls/io.url';
import { EInputOutputUrls } from 'data/urls/inputOutput.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import {
  ioTransactionGetConverter,
  ioTransactionsFilterConverter,
  ioTransactionsFiltersConverter,
} from 'data/convertors/inputOutput.convertors';
import { downloadFile } from 'redux/services/fileServices';

const transactionsUrl = `${EIoUrls.IO}/${EIoUrls.TRANSACTIONS}`;
const categoriesUrl = `${EInputOutputUrls.IO}/${EInputOutputUrls.CATEGORIES}`;
const uploadUrl = `${EIoUrls.IO}/${EIoUrls.DOCUMENTS}/${EIoUrls.UPLOAD}`;
const createIoUrl = `${EIoUrls.IO}`;
const exportTransactionsUrl = `${EIoUrls.IO}/${EIoUrls.TRANSACTIONS}/export`;

export const ioApi = authSplitApi('io', [
  'io-transactions',
  'ioCategories',
  'io-documents',
]).injectEndpoints({
  endpoints: build => ({
    getIoTransactions: build.query<
      TIoTransactionsListUI,
      GetIoTransactionsParams
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
        sourceId,
        sourceType,
      }) {
        return {
          url: transactionsUrl,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sourceType === 'WORKSHOP' || sourceType === 'EVENT'
              ? sourceId && { sourceId }
              : sourceId && { participantIds: sourceId }),
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && ioTransactionsFilterConverter(filters)),
          },
        };
      },
      providesTags: ['io-transactions'],
      transformResponse: (response: TIoTransactionsListDB) => {
        const pageable =
          response?.data || ({} as TPageableDataWithContentDB<unknown[]>);
        const content =
          (pageable.content as unknown as any[] | undefined) || [];
        return {
          ...pagableDataConverter.fromDb(pageable),
          content: content.map(item =>
            ioTransactionGetConverter.fromDb(item as any),
          ),
          ...(response?.filters && {
            filters: ioTransactionsFiltersConverter.fromDb(response.filters),
          }),
        } as TIoTransactionsListUI;
      },
    }),

    exportIoTransactions: build.query<
      TExportCsvUI,
      {
        sourceId: string;
        sourceType: string;
        resourceIds?: string[];
        inKind?: boolean;
      }
    >({
      query: ({ sourceId, sourceType, resourceIds, inKind }) => ({
        url: exportTransactionsUrl,
        method: 'GET',
        params: {
          ...(sourceType === 'WORKSHOP' || sourceType === 'EVENT'
            ? sourceId && { sourceId }
            : sourceId && { participantIds: sourceId }),
          ...(resourceIds && resourceIds.length > 0 && { resourceIds }),
          ...(inKind && { inKind: true }),
        },
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),

    exportIoTransactionsPost: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters?: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: exportTransactionsUrl,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),

    createInputOutput: build.mutation<
      TCreateInputOutputResponse,
      TCreateInputOutputRequest
    >({
      query: body => ({
        url: createIoUrl,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['io-transactions'],
    }),

    updateInputOutput: build.mutation<
      void,
      { resourceId: string; body: TUpdateInputOutputRequest }
    >({
      query: ({ resourceId, body }) => ({
        url: `${EIoUrls.IO}/${resourceId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['io-transactions'],
    }),

    getIoCategories: build.query<
      TIoCategoryOption[],
      { type: TCategoryTypeEnum }
    >({
      query: ({ type }) => ({
        url: categoriesUrl,
        method: 'GET',
        params: { type },
      }),
      providesTags: ['ioCategories'],
      transformResponse: (data: TIoCategoryDb[] = []) =>
        data.map(item => ({
          label: item.categoryName,
          value: item.resourceId,
          isAeyc: item.isAeyc ?? false,
        })),
    }),

    createIoCategory: build.mutation<
      TIoCategoryOption,
      { type: TCategoryTypeEnum; categoryName: string }
    >({
      query: body => ({
        url: categoriesUrl,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TIoCategoryDb) => ({
        label: data.categoryName,
        value: data.resourceId,
        isAeyc: data.isAeyc ?? false,
      }),
    }),

    uploadIoDocument: build.mutation<TIoDocumentResponse, TUploadIoDocumentUI>({
      query(body) {
        const formData = new FormData();
        formData.append('file', body.file);

        return {
          url: uploadUrl,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['io-documents'],
    }),

    deleteInputOutput: build.mutation<void, { resourceId: string }>({
      query: ({ resourceId }) => ({
        url: `${EIoUrls.IO}/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['io-transactions'],
    }),

    deleteIoDocuments: build.mutation<void, TDeleteIoDocumentsUI>({
      query(body) {
        return {
          url: `/${EIoUrls.STORAGE}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['io-documents'],
    }),

    getIoTransactionById: build.query<TInputOutputRow, { resourceId: string }>({
      query: ({ resourceId }) => ({
        url: `${EIoUrls.IO}/${resourceId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) =>
        ioTransactionGetConverter.fromDb(response),
    }),
  }),
});

export const {
  useGetIoTransactionsQuery,
  useLazyGetIoTransactionsQuery,
  useExportIoTransactionsQuery,
  useLazyExportIoTransactionsQuery,
  useExportIoTransactionsPostMutation,
  useCreateInputOutputMutation,
  useUpdateInputOutputMutation,
  useDeleteInputOutputMutation,
  useGetIoCategoriesQuery,
  useCreateIoCategoryMutation,
  useUploadIoDocumentMutation,
  useDeleteIoDocumentsMutation,
  useLazyGetIoTransactionByIdQuery,
} = ioApi;
